import Skeleton from '@material-ui/lab/Skeleton';
import { cloneDeep, isNil } from 'lodash';
import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { getIasFilterOptionsList } from '~/src/dash/api/ias-filter';
import { IReportFilter } from '~/src/dash/apiV2/reporting/reportTypes';
import {
  getSimpleDropdownOptionSelects,
  getSimpleSingleOptionSelects,
  ISimpleOptionSelect,
} from '~/src/dash/components/common';
import { ISimpleDropdownOptionSelect } from '~/src/dash/components/dropdowns/SimpleDropdown';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { spacingV2 } from '~/src/dash/components/layout/common';
import { getLineHeight } from '~/src/dash/components/text/common';
import { TextWithTooltip } from '~/src/dash/components/text/TextWithTooltip';
import {
  borderRadius,
  Col,
  Cols,
  Row,
  Rows,
  Text,
  TextButton,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { IListItem, IMenuList } from '~/src/dash/types/menuList';
import { publicaColors } from '~/src/dash/utils/colors';
import { generateStatesMenuList } from '~/src/dash/utils/countries';
import { getStartDate } from '~/src/dash/utils/date';
import { createReportingDeviceTypesMenuList } from '~/src/dash/utils/devices';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import {
  IncludeExcludeStatus,
  IReportFilterState,
  REPORTING_MAX_SELECTED_CHARS,
} from '~/src/dash/views/reporting/ReportBuilder/common';
import { useReportBuilderStore } from '~/src/dash/views/reporting/ReportBuilder/context';
import {
  convertListItemsToDropdownSelectOptions,
  FilterRow,
} from '~/src/dash/views/reporting/ReportBuilder/Filters/FilterRow';
import { REPORT_FILTERS } from '~/src/dash/views/reporting/ReportBuilder/Filters/filters';
import { IAS_REPORT_FILTERS } from '~/src/dash/views/reporting/ReportBuilder/Filters/ias-filters';

interface IFiltersSection {
  // holds data about filters from the backend
  filters: IReportFilter[];
  selectableFilters: string[];
  // holds what filters are selected and which items for each filters are selected
  selectedFilters: IReportFilterState[];
  setSelectedFilters: (filters: IReportFilterState[]) => void;
  onError: (err: any, msg: string) => void;
}

export interface IReportFilterData {
  key: string;
  list?: IListItem[];
}

export const FiltersSection = ({
  filters,
  onError,
  selectableFilters,
  selectedFilters,
  setSelectedFilters,
}: IFiltersSection) => {
  const { t } = usePublicaTranslation();
  const { endDate, reportTypeID, startDate, timezone } = useReportBuilderStore();
  // holds the data (selectable items) for each filter
  const [filtersData, setFiltersData] = useState<IReportFilterData[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const selectedFilterNames = selectedFilters
    .map((filter) => filters.find((reportFilter) => filter.key === reportFilter.Key)?.Label)
    .join(', ');

  const { session, user } = useBaseStore();

  useEffect(() => {
    getFilters();
  }, []);

  // edit states data when selected countries changes
  useEffect(() => {
    const countryFilter = selectedFilters.find((f) => f.key === 'countries');
    if (countryFilter) {
      const filtersDataCopy = cloneDeep(filtersData);
      const selectedStates = selectedFilters.find((f) => f.key === 'states')?.selected;
      const statesFilterData = filtersDataCopy.findIndex((f) => f.key === 'states');
      if (statesFilterData > -1) {
        if (countryFilter.selected.length === 1) {
          filtersDataCopy[statesFilterData].list = Object.values(
            generateStatesMenuList(countryFilter.selected[0], selectedStates || []),
          );
        } else {
          filtersDataCopy[statesFilterData].list = [];
        }
      }
      setFiltersData(filtersDataCopy);
    }
  }, [selectedFilters]);

  // update device types filter options when reportType changes
  useEffect(() => {
    const filtersDataCopy = cloneDeep(filtersData);
    const deviceTypesFilterDataIdx = filtersDataCopy.findIndex((f) => f.key === 'devicetypes');
    if (deviceTypesFilterDataIdx > -1) {
      filtersDataCopy[deviceTypesFilterDataIdx].list = Object.values(
        createReportingDeviceTypesMenuList(reportTypeID),
      );
      setFiltersData(filtersDataCopy);
    }
  }, [reportTypeID]);

  // update Line Items filter options when the time range changes
  useEffect(() => {
    if (!isLoading) {
      getFilter('campaign_ids');
    }
  }, [startDate, endDate, timezone]);

  // fetch all filters
  const getFilters = () => {
    const apiCalls: Array<Promise<Record<string, IMenuList>>> = [];
    const apiFilterKeys: string[] = [];
    const filtersDataCopy = cloneDeep(filtersData);

    REPORT_FILTERS.forEach((filter) => {
      if (filter.getList) {
        apiCalls.push(
          filter.getList(session, user, {
            startDate,
            endDate,
            timezone,
          }),
        );
        apiFilterKeys.push(filter.key);
      }
      if (filter.list) {
        filtersDataCopy.push({
          key: filter.key,
          list: Object.values(filter.list),
        });
      }
    });

    if (session.publisher.IASTeamID > 0) {
      IAS_REPORT_FILTERS.forEach((filter) => {
        if (filter.getList) {
          apiCalls.push(
            filter.getList(
              session,
              filter.key,
              getStartDate('America/New_York', 7, 'day').startOf('day'),
              getStartDate('America/New_York', 1, 'day').endOf('day'),
            ),
          );
          apiFilterKeys.push(filter.key);
        }
        if (filter.list) {
          filtersDataCopy.push({
            key: filter.key,
            list: Object.values(filter.list),
          });
        }
      });
    }

    Promise.all(apiCalls)
      .then((apiFilters) => {
        apiFilters.forEach((filterData, idx) => {
          filtersDataCopy.push({
            key: apiFilterKeys[idx],
            list: Object.values(Object.values(filterData)[0]),
          });
        });

        setFiltersData(filtersDataCopy);
      })
      .catch((e) => onError(e, t('ErrorLoadingItem', { item: t('Filters') })))
      .finally(() => setIsLoading(false));
  };

  // fetch a single filter
  const getFilter = (key: string) => {
    const filtersDataCopy = cloneDeep(filtersData);
    const filter = REPORT_FILTERS.find((item) => item.key === key);
    const filterIdx = filtersDataCopy.findIndex((f) => f.key === key);
    if (!filter || isNil(filterIdx)) return;

    setIsLoading(true);
    if (filter.list) {
      filtersDataCopy[filterIdx] = {
        key,
        list: Object.values(filter.list),
      };
      setFiltersData(filtersDataCopy);
      setIsLoading(false);
      return;
    }

    if (filter.getList) {
      filter
        .getList(session, user, {
          startDate,
          endDate,
          timezone,
        })
        .then((filterOptions) => {
          filtersDataCopy[filterIdx] = {
            key,
            list: Object.values(Object.values(filterOptions)[0]),
          };
          setFiltersData(filtersDataCopy);
        })
        .catch((e) => onError(e, t('ErrorLoadingItem', { item: t('Filters') })))
        .finally(() => setIsLoading(false));
    }
  };

  const onAddFilter = () => {
    const selectedFilterKeys = selectedFilters.map((filter) => filter.key);
    const firstUnselectedFilter = filters.find(
      (item) => !selectedFilterKeys.includes(item.Key) && selectableFilters.includes(item.Key),
    );
    if (firstUnselectedFilter) {
      const selectedFiltersCopy = cloneDeep(selectedFilters);
      selectedFiltersCopy.push({
        key: firstUnselectedFilter.Key,
        selected: [],
        includeExcludeStatus: firstUnselectedFilter?.IsIncludeExcludeEnabled
          ? IncludeExcludeStatus.INCLUDE
          : IncludeExcludeStatus.NONE,
        isHideInactive: false,
      });

      unstable_batchedUpdates(() => {
        setSelectedFilters(selectedFiltersCopy);
      });
    }
  };

  const onIncludeExcludeChange = (filterKey: string) => {
    const selectedFiltersCopy = cloneDeep(selectedFilters);
    const foundIdx = selectedFiltersCopy.findIndex((filter) => filter.key === filterKey);
    if (foundIdx > -1) {
      if (selectedFiltersCopy[foundIdx].includeExcludeStatus === IncludeExcludeStatus.INCLUDE) {
        selectedFiltersCopy[foundIdx].includeExcludeStatus = IncludeExcludeStatus.EXCLUDE;
      } else {
        selectedFiltersCopy[foundIdx].includeExcludeStatus = IncludeExcludeStatus.INCLUDE;
      }
      setSelectedFilters(selectedFiltersCopy);
    }
  };

  const getUnselectedFilters = (selectedFilterKey: string): ISimpleOptionSelect[] => {
    const filterKeys = filters.map((filter) => filter.Key);
    const selectedFilterKeys = selectedFilters.map((filter) => filter.key);
    const unselectedFilters = filterKeys
      .filter((filterKey) => !selectedFilterKeys.includes(filterKey))
      .filter((filterKey) => selectableFilters.includes(filterKey))
      .map((key) => {
        return {
          key,
          label: filters.find((filter) => filter.Key === key)?.Label || '',
          value: key,
        };
      });
    unselectedFilters.push({
      key: selectedFilterKey,
      label: filters.find((filter) => filter.Key === selectedFilterKey)?.Label || '',
      value: selectedFilterKey,
    });

    return getSimpleSingleOptionSelects(unselectedFilters, selectedFilterKey);
  };

  const getFilterOptions = (
    filterList: IListItem[],
    selectedValues: string[],
    IsHideInactiveEnabled: boolean,
  ): ISimpleDropdownOptionSelect[] => {
    return getSimpleDropdownOptionSelects(
      convertListItemsToDropdownSelectOptions(filterList, IsHideInactiveEnabled),
      selectedValues,
    );
  };

  const onValueChange = (input: string | string[], key: string, inputType: string) => {
    const newSelectedFilters = cloneDeep(selectedFilters);
    const filterToBeUpdated = newSelectedFilters.find((filter) => filter.key === key); // find the filter that need to be updated

    if (filterToBeUpdated) {
      if (inputType === 'input' && typeof input === 'string') {
        // if text input, change selected to that input
        filterToBeUpdated.selected = [input];
      } else if (inputType === 'list' && Array.isArray(input)) {
        filterToBeUpdated.selected = input;
      }
      setSelectedFilters(newSelectedFilters);
    }
  };

  const onKeyChange = (selectedID: string, unselectedID: string) => {
    const selectedFiltersCopy = cloneDeep(selectedFilters);
    const foundFilterIdx = selectedFiltersCopy.findIndex((filter) => filter.key === unselectedID);
    const filter = filters.find((f) => f.Key === selectedID);
    if (foundFilterIdx > -1) {
      selectedFiltersCopy[foundFilterIdx] = {
        key: selectedID,
        selected: [],
        includeExcludeStatus: filter?.IsIncludeExcludeEnabled
          ? IncludeExcludeStatus.INCLUDE
          : IncludeExcludeStatus.NONE,
        isHideInactive: false,
      };
    } else {
      selectedFiltersCopy.push({
        key: selectedID,
        selected: [],
        includeExcludeStatus: filter?.IsIncludeExcludeEnabled
          ? IncludeExcludeStatus.INCLUDE
          : IncludeExcludeStatus.NONE,
        isHideInactive: false,
      });
    }
    setSelectedFilters(selectedFiltersCopy);
  };

  const onHideInactiveChange = (key: string) => {
    const selectedFiltersCopy = cloneDeep(selectedFilters);

    const filterIdx = selectedFiltersCopy.findIndex((f) => f.key === key);
    if (filterIdx > -1) {
      selectedFiltersCopy[filterIdx].isHideInactive =
        !selectedFiltersCopy[filterIdx].isHideInactive;
    }

    setSelectedFilters(selectedFiltersCopy);
  };

  const onRemoveFilter = (id: string) => {
    const selectedFiltersCopy = cloneDeep(selectedFilters);
    selectedFiltersCopy.splice(
      selectedFiltersCopy.findIndex((filter) => filter.key === id),
      1,
    );
    setSelectedFilters(selectedFiltersCopy);
  };

  const onClearDropdown = (key: string) => {
    const selectedFiltersCopy = cloneDeep(selectedFilters);
    const foundIdx = selectedFiltersCopy.findIndex((filter) => filter.key === key);
    if (foundIdx > -1) {
      selectedFiltersCopy[foundIdx].selected = [];
      setSelectedFilters(selectedFiltersCopy);
    }
  };

  const onFilterSearch = (key: string) => {
    const onFilterSearchInner = (search: string): void => {
      getIasFilterOptionsList(
        session,
        key,
        getStartDate('America/New_York', 7, 'day').startOf('day'),
        getStartDate('America/New_York', 1, 'day').endOf('day'),
        search,
      ).then((newFilterData) => {
        const filtersDataCopy = cloneDeep(filtersData);
        const idx = filtersDataCopy.findIndex((filterData) => filterData.key === key);
        if (idx >= 0) {
          filtersDataCopy[idx].list = Object.values(Object.values(newFilterData)[0]);
        }
        setFiltersData(filtersDataCopy);
      });
    };

    return onFilterSearchInner;
  };

  return (
    <Rows>
      <Row>
        <Cols gap={spacingV2.medium}>
          <Col style={{ display: 'flex', alignItems: 'center' }}>
            <IconV2 name='filter' />
          </Col>
          <Col extraSmallVerticalPaddingV2>
            <Text tKey='Filters' sizeV2 bold large />
          </Col>
          {selectedFilters.length > 0 && (
            <>
              <Col
                style={{ display: 'flex', alignItems: 'center', paddingLeft: spacingV2.extraLarge }}
              >
                <Text tKey='NumSelected' tProps={{ count: selectedFilters.length }} small bold />
              </Col>
              <Col style={{ display: 'flex', alignItems: 'center' }}>
                <TextWithTooltip
                  small
                  tooltip={`${selectedFilterNames}`}
                  style={{
                    borderRadius: borderRadius.base,
                    backgroundColor: publicaColors.veryLightBlue,
                    padding: `${spacingV2.extraSmall}`,
                  }}
                >
                  {selectedFilterNames.length < REPORTING_MAX_SELECTED_CHARS
                    ? `${selectedFilterNames}`
                    : `${selectedFilterNames.slice(0, REPORTING_MAX_SELECTED_CHARS)}...`}
                </TextWithTooltip>
              </Col>
              <Col style={{ display: 'flex', alignItems: 'center' }}>
                <TextButton size='small' weight='none' onClick={() => setSelectedFilters([])}>
                  <Text small tKey='Clear' style={{ color: 'inherit', fontWeight: 'inherit' }} />
                </TextButton>
              </Col>
            </>
          )}
        </Cols>
      </Row>
      {!isLoading ? (
        <Rows style={{ padding: `${spacingV2.extraSmall} 3rem` }}>
          {filters.length < 1 && (
            <Row noHorizontalPaddingV2 paddingTop={spacingV2.none} paddingBottom={spacingV2.base}>
              <Text tKey='ReportingFilterNotEnabled' small muted />
            </Row>
          )}
          {filters.length > 0 &&
            selectedFilters.length > 0 &&
            selectedFilters.map((filterState) => {
              const filterKey = filterState.key;
              const filterData = filters.find((filter) => filter.Key === filterKey);
              const filterList = filtersData.find((filter) => filter.key === filterKey)?.list;
              const countryFilter = selectedFilters.find((f) => f.key === 'countries');
              const isOneCountrySelected = countryFilter && countryFilter.selected.length === 1;

              const foundIasFilter = IAS_REPORT_FILTERS.find((filter) => filter.key === filterKey);
              const iasIasFilterSearchEnabled =
                foundIasFilter && !!foundIasFilter.getList && session.publisher.IASTeamID > 0;

              const tooltip =
                filterKey === 'states' && !isOneCountrySelected
                  ? t('ReportingTooltipFilterState')
                  : '';
              const selectableFiltersList = getUnselectedFilters(filterKey);

              return (
                <Row
                  smallVerticalPaddingV2
                  style={{
                    width: '100%',
                    maxWidth: '60rem',
                    minWidth: '22.5rem',
                    backgroundColor: `${publicaColors.veryLightBlue}45`,
                    marginBottom: spacingV2.small,
                    borderRadius: borderRadius.small,
                    border: `1px solid ${publicaColors.veryLightGray}`,
                  }}
                >
                  <FilterRow
                    keys={selectableFiltersList}
                    onKeyChange={(key: string) => onKeyChange(key, filterKey)}
                    values={getFilterOptions(
                      filterList || [],
                      filterState.selected,
                      Boolean(filterData?.IsHideInactiveEnabled),
                    )}
                    onValueChange={(input) =>
                      onValueChange(input, filterKey, filterData?.Type || '')
                    }
                    onFilterSearch={
                      iasIasFilterSearchEnabled ? onFilterSearch(filterKey) : undefined
                    }
                    valueType={filterData?.Type || ''}
                    valueLabel={filterData?.Label || ''}
                    valueInput={filterState.selected[0]}
                    isIDDisplayed={filterData?.IsIDDisplayed}
                    isHideInactive={filterState.isHideInactive}
                    isHideInactiveEnabled={filterData?.IsHideInactiveEnabled}
                    onHideInactiveChange={() => onHideInactiveChange(filterKey)}
                    isIncludeExclude={filterState.includeExcludeStatus}
                    isIncludeExcludeEnabled={filterData?.IsIncludeExcludeEnabled}
                    onIncludeExcludeChange={() => onIncludeExcludeChange(filterKey)}
                    isSelectAllEnabled={filterData?.IsSelectAllEnabled}
                    onClear={() => onClearDropdown(filterKey)}
                    onRemoveRow={() => onRemoveFilter(filterKey)}
                    tooltip={tooltip}
                    maxSelectedItems={filterData?.MaxSelectedItems}
                  />
                </Row>
              );
            })}
          {filters.length > 0 && selectedFilters.length < selectableFilters.length && (
            <Row noHorizontalPaddingV2 paddingTop={spacingV2.none} paddingBottom={spacingV2.base}>
              <TextButton size='small' onClick={onAddFilter}>
                <Text small tKey='AddFilter' style={{ color: 'inherit', fontWeight: 'inherit' }} />
              </TextButton>
            </Row>
          )}
        </Rows>
      ) : (
        <Skeleton
          variant='text'
          animation='wave'
          width='100%'
          height={getLineHeight('extraLarge')}
        />
      )}
    </Rows>
  );
};
